import { joinClassNames } from '@vertex-protocol/web-common';
import { Divider, Input } from '@vertex-protocol/web-ui';
import { ActionSummary } from 'client/components/ActionSummary';
import { FractionAmountButtons } from 'client/components/FractionAmountButtons';
import { BridgeModeSelector } from 'client/modules/collateral/nadoDeposit/components/BridgeModeSelector';
import { NadoDepositError } from 'client/modules/collateral/nadoDeposit/components/NadoDepositError';
import { TokenIcon } from 'client/modules/collateral/nadoDeposit/components/NadoTokenSelect';
import { ChainIcon } from 'client/modules/collateral/nadoDeposit/components/NadoChainSelect';
import { DEPOSIT_CHAIN_BY_ID } from 'client/modules/collateral/nadoDeposit/config/chains';
import { NadoWithdrawProgress } from './NadoWithdrawProgress';
import { NadoWithdrawSubmitButton } from './NadoWithdrawSubmitButton';
import {
  NadoWithdrawFormActions,
  NadoWithdrawFormData,
} from '../types';

interface Props extends NadoWithdrawFormData, NadoWithdrawFormActions {
  onBack: () => void;
  selectedFraction?: number;
}

/**
 * Step 2 of the Nado withdrawal flow.
 *
 * Shows:
 *  - Amount input with token icon (always USDT0 since withdrawing from Ink)
 *  - Destination chain label
 *  - Available balance (max withdrawable from Nado)
 *  - Fraction buttons
 *  - Fee summary (cross-chain only)
 *  - Destination token info (USDT on Ethereum, USDT0 on Arbitrum)
 *  - Submit button
 *  - Bridge progress (shown after OFT send)
 */
export function NadoWithdrawAmountStep({
  selectedChainId,
  selectedTokenSymbol,
  destinationTokenSymbol,
  amount,
  availableBalance,
  estimatedReceiveAmount,
  lzFee,
  withdrawalFee,
  isOnInk,
  buttonState,
  bridgeProvider,
  bridgeMode,
  showBridgeModeSelector,
  acrossFee,
  isAcrossQuoteLoading,
  bridgeTxHash,
  inputError,
  errorMessage,
  errorDetails,
  isFundingAutomationAvailable,
  isFundingAutomationActive,
  onAmountChange,
  onFractionSelected,
  onBridgeModeChange,
  onSubmit,
  onStartFundingAutomation,
  onBack,
  selectedFraction,
}: Props) {
  const destChain = DEPOSIT_CHAIN_BY_ID[selectedChainId];
  const withdrawTokenSymbol = selectedTokenSymbol;

  // Cross-chain withdrawals always show fees (even if bridge fee is 0)
  const isCrossChain = destChain ? !destChain.isPrimary : false;
  const hasAnyFee = isCrossChain || !!withdrawalFee;

  // Calculate net receive amount (amount - withdrawal fee)
  const netReceiveAmount = (() => {
    if (!amount || !withdrawalFee) return amount || '0.00';
    try {
      const inputNum = parseFloat(amount);
      const feeNum = parseFloat(withdrawalFee);
      if (isNaN(inputNum) || isNaN(feeNum)) return amount;
      const net = Math.max(0, inputNum - feeNum);
      return net.toFixed(6);
    } catch {
      return amount;
    }
  })();

  function handleAvailableClick() {
    onFractionSelected(1);
  }

  return (
    <div className="flex flex-col gap-y-4">
      {/* Amount input */}
      <div
        className={joinClassNames(
          'bg-surface-2 flex flex-col gap-y-3 rounded-sm p-3',
        )}
      >
        {/* Token + input row */}
        <div className="flex items-center gap-x-3">
          <div className="flex items-center gap-x-2 flex-shrink-0">
            <TokenIcon symbol={withdrawTokenSymbol} />
            <span className="text-text-primary text-sm font-medium">
              {withdrawTokenSymbol}
            </span>
          </div>
          <Input.TextArea
            type="number"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            disabled={isFundingAutomationActive}
            placeholder="0.00"
            className="text-text-primary text-right text-lg focus:outline-none min-w-0"
            min={0}
            step="any"
          />
        </div>

        {/* Destination chain label */}
        {destChain && (
          <div className="flex items-center gap-x-1.5">
            <span className="text-text-tertiary text-xs">Withdraw to:</span>
            <ChainIcon chainId={destChain.chainId} />
            <span className="text-text-secondary text-xs">{destChain.name}</span>
          </div>
        )}

        {/* Destination token info */}
        {destChain && (
          <div className="flex items-center gap-x-1.5">
            <span className="text-text-tertiary text-xs">You will receive:</span>
            <TokenIcon symbol={destinationTokenSymbol} />
            <span className="text-text-secondary text-xs">
              {destinationTokenSymbol} on {destChain.name}
            </span>
          </div>
        )}

        {/* Available balance */}
        <button
          type="button"
          onClick={handleAvailableClick}
          disabled={isFundingAutomationActive}
          className={joinClassNames(
            'text-text-tertiary flex items-center gap-x-1 text-xs transition-colors self-start',
            isFundingAutomationActive
              ? 'cursor-not-allowed opacity-60'
              : 'hover:text-text-secondary',
          )}
        >
          <span>Available:</span>
          <span className="text-text-secondary font-medium">
            {availableBalance} {withdrawTokenSymbol}
          </span>
        </button>

        {/* Input validation error */}
        {inputError && (
          <p className="text-negative text-xs">{inputError}</p>
        )}
      </div>

      {/* Fraction buttons */}
      <FractionAmountButtons
        selectedFraction={selectedFraction}
        disabled={isFundingAutomationActive}
        onFractionSelected={onFractionSelected}
      />

      {/* Bridge mode selector (USDC cross-chain only, hidden after bridge completes) */}
      {showBridgeModeSelector && !['bridging', 'bridge_pending', 'success'].includes(buttonState) && (
        <BridgeModeSelector
          selectedMode={bridgeMode}
          onModeChange={onBridgeModeChange}
          acrossFee={acrossFee}
          isLoading={isAcrossQuoteLoading}
          disabled={isFundingAutomationActive || !['idle', 'ready_withdraw'].includes(buttonState)}
        />
      )}

      {/* Fee summary + submit button */}
      <ActionSummary.Container>
        {hasAnyFee && (
          <>
            <div className="flex flex-col gap-y-1.5 px-3 pt-3">
              {isCrossChain && (
                <FeeRow
                  label="Bridge Fee"
                  value={
                    bridgeProvider === 'across'
                      ? (acrossFee ?? '~$0')
                      : bridgeProvider === 'cctp'
                        ? 'Free (CCTP)'
                        : lzFee === 'Loading...'
                          ? lzFee
                          : `~${lzFee} ETH`
                  }
                />
              )}
              {withdrawalFee && (
                  <FeeRow
                    label="Partner DEX Withdrawal Fee"
                    value={`${withdrawalFee} ${withdrawTokenSymbol}`}
                  />
              )}
              <Divider className="my-0.5" />
              <FeeRow
                label="You will receive"
                value={`~${estimatedReceiveAmount || netReceiveAmount} ${destinationTokenSymbol}`}
                isHighlighted
              />
            </div>
            <Divider />
          </>
        )}
        <div className="flex flex-col gap-y-2 p-2">
          <NadoWithdrawSubmitButton
            state={buttonState}
            chainName={destChain?.name}
            inputError={inputError}
            bridgeProvider={bridgeProvider}
            tokenSymbol={selectedTokenSymbol}
            isFundingAutomationAvailable={isFundingAutomationAvailable}
            isFundingAutomationActive={isFundingAutomationActive}
            onClick={isFundingAutomationAvailable ? onStartFundingAutomation : onSubmit}
          />
          {errorMessage && (
            <NadoDepositError
              message={errorMessage}
              details={errorDetails}
            />
          )}
        </div>
      </ActionSummary.Container>

      {/* 3-step progress tracker */}
      <NadoWithdrawProgress
        state={buttonState}
        chainName={destChain?.name}
        bridgeProvider={bridgeProvider}
        bridgeTxHash={bridgeTxHash}
        tokenSymbol={selectedTokenSymbol}
        bridgeFee={bridgeProvider === 'oft' ? `~${lzFee} ETH` : bridgeProvider === 'across' ? acrossFee : undefined}
      />
    </div>
  );
}

function FeeRow({
  label,
  value,
  isHighlighted,
}: {
  label: string;
  value: string;
  isHighlighted?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-x-4">
      <span className="text-text-secondary text-xs">{label}</span>
      <span
        className={
          isHighlighted
            ? 'text-text-primary text-sm font-medium'
            : 'text-text-secondary text-xs'
        }
      >
        {value}
      </span>
    </div>
  );
}
